import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { ScannerData } from '../types/ScannerData';
import { formatDate, parseForwardDateRange } from '../utils/dateUtils';

export interface DateFilter {
  field: string;
  operator: 'equals' | 'contains' | 'greater_than' | 'less_than' | 'between' | 'date_range';
  value: string | { start: string; end: string };
}

interface DateFilterContextType {
  dateFilter: DateFilter | null;
  setDateFilter: (filter: DateFilter | null) => void;
  clearDateFilter: () => void;
  isDateInFilter: (date: any) => boolean;
  applyDateFilter: (data: ScannerData[]) => ScannerData[];
}

const DateFilterContext = createContext<DateFilterContextType | undefined>(undefined);

export const useDateFilter = () => {
  const context = useContext(DateFilterContext);
  if (!context) {
    throw new Error('useDateFilter must be used within a DateFilterProvider');
  }
  return context;
};

interface DateFilterProviderProps {
  children: React.ReactNode;
}

export const DateFilterProvider: React.FC<DateFilterProviderProps> = ({ children }) => {
  // Default to "All Data" (no filter)
  const [dateFilter, setDateFilter] = useState<DateFilter | null>(null);

  const clearDateFilter = useCallback(() => {
    // Reset to "All Data" (no filter)
    setDateFilter(null);
  }, [setDateFilter]);

  const isDateInFilter = useCallback((date: any) => {
    if (!dateFilter) return true; // Default to true when no filter (show all data)

    const dateStr = formatDate(date);

    switch (dateFilter.operator) {
      case 'equals':
        return dateStr === dateFilter.value;
      case 'contains':
        return typeof dateFilter.value === 'string' && dateStr.includes(dateFilter.value);
      case 'greater_than':
        return typeof dateFilter.value === 'string' && dateStr > dateFilter.value;
      case 'less_than':
        return typeof dateFilter.value === 'string' && dateStr < dateFilter.value;
      case 'between':
        if (typeof dateFilter.value === 'object' && 'start' in dateFilter.value && 'end' in dateFilter.value) {
          return dateStr >= dateFilter.value.start && dateStr <= dateFilter.value.end;
        }
        return true;
      case 'date_range':
        if (typeof dateFilter.value === 'string') {
          // Handle preset date ranges for options expiry (forward-looking)
          const dateRange = parseForwardDateRange(dateFilter.value);
          if (dateRange) {
            return dateStr >= dateRange.startDate && dateStr <= dateRange.endDate;
          }
        }
        return true;
      default:
        return true;
    }
  }, [dateFilter]);

  const applyDateFilter = useMemo(() => {
    return (data: ScannerData[]) => {
      if (!dateFilter) return data; // Return all data when no filter (show all data)

      return data.filter(item => {
        const dateField = item['Expiry Date'] || item['expiry_date'] || item['expiryDate'];
        return isDateInFilter(dateField);
      });
    };
  }, [dateFilter, isDateInFilter]);

  const value: DateFilterContextType = {
    dateFilter,
    setDateFilter,
    clearDateFilter,
    isDateInFilter,
    applyDateFilter,
  };

  return (
    <DateFilterContext.Provider value={value}>
      {children}
    </DateFilterContext.Provider>
  );
}; 